import { getRouteApi } from "@tanstack/react-router";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getGuide, GUIDES, wordCount } from "@/data/guides";
import { SERVICES, type ServiceId } from "@/data/services";
import { speakableSchema, BUILD_DATE } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { BookOpen, Clock, Calendar, ChevronRight, ExternalLink } from "lucide-react";

const routeApi = getRouteApi("/guide/$slug");

export function GuidePage() {
  const { slug } = routeApi.useLoaderData() as { slug: string };
  const g = getGuide(slug)!;
  const wc = wordCount(g);
  const related = g.relatedServiceSlugs.map((id) => SERVICES[id as ServiceId]).filter(Boolean);
  const otherGuides = GUIDES.filter((x) => x.slug !== g.slug);

  return (
    <main className="min-h-screen bg-gradient-to-b from-navy-deep via-navy to-navy-deep text-white">
      <section className="relative overflow-hidden border-b border-white/10 px-4 py-12 sm:px-6 sm:py-16">
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,hsl(var(--gold)/0.18),transparent_60%)]"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-4xl space-y-5">
          <nav className="text-xs text-white/80">
            <Link to="/" className="hover:text-gold">
              หน้าแรก
            </Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/guides" className="hover:text-gold">
              Ultimate Guides
            </Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <span className="text-white/80">{g.category}</span>
          </nav>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-2.5 py-0.5 font-bold uppercase tracking-widest text-gold">
              <BookOpen className="h-3 w-3" /> Ultimate Guide · {g.category}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-0.5 text-white/80">
              <Clock className="h-3 w-3" /> {g.readMinutes} นาที · {wc.toLocaleString()} คำ
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-0.5 text-white/80">
              <Calendar className="h-3 w-3" /> อัปเดต {g.updated}
            </span>
          </div>
          <h1 className="text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {g.h1}
          </h1>
          <p className="aeo-speakable max-w-3xl rounded-2xl border border-gold/30 bg-gold/5 p-5 text-sm leading-relaxed text-white/90 sm:text-base">
            <span className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-gold">
              TL;DR · AI Quick Answer
            </span>
            {g.tldr}
          </p>
        </div>
      </section>

      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[260px_1fr]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gold">
              สารบัญ
            </div>
            <ol className="space-y-2 text-sm">
              {g.sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="block text-white/75 hover:text-gold">
                    {s.heading}
                  </a>
                </li>
              ))}
              <li>
                <a href="#howto" className="block text-white/75 hover:text-gold">
                  วิธีทำ (HowTo)
                </a>
              </li>
              <li>
                <a href="#faq" className="block text-white/75 hover:text-gold">
                  คำถามที่พบบ่อย
                </a>
              </li>
              <li>
                <a href="#sources" className="block text-white/75 hover:text-gold">
                  แหล่งอ้างอิง
                </a>
              </li>
            </ol>
          </div>
        </aside>

        <article className="space-y-12">
          <p className="aeo-speakable text-base leading-relaxed text-white/85 sm:text-lg">
            {g.intro}
          </p>

          {g.sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24 space-y-3">
              <h2 className="text-2xl font-bold text-white sm:text-3xl">{s.heading}</h2>
              {s.paragraphs.map((p, i) => (
                <p key={i} className="text-[15px] leading-relaxed text-white/82">
                  {p}
                </p>
              ))}
              {s.bullets && s.bullets.length > 0 && (
                <ul className="grid gap-2 sm:grid-cols-2">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/85"
                    >
                      <span className="mt-0.5 text-gold">→</span> {b}
                    </li>
                  ))}
                </ul>
              )}
              {s.callout && (
                <div className="rounded-xl border border-gold/30 bg-gold/10 p-4 text-sm text-white/90">
                  <span className="mr-2 font-bold text-gold">หมายเหตุ:</span>
                  {s.callout}
                </div>
              )}
            </section>
          ))}

          <section id="howto" className="scroll-mt-24 space-y-4">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">วิธีทำแบบขั้นตอน</h2>
            <p className="text-white/80">{g.howTo.description}</p>
            <ol className="space-y-3">
              {g.howTo.steps.map((st, i) => (
                <li key={st.name} className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold text-sm font-bold text-navy-deep">
                      {i + 1}
                    </span>
                    <div>
                      <div className="font-bold text-white">{st.name}</div>
                      <div className="mt-1 text-sm text-white/75">{st.text}</div>
                      {st.duration && (
                        <div className="mt-1 text-[11px] text-gold/80">
                          ใช้เวลาประมาณ: {st.duration}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section id="faq" className="scroll-mt-24 space-y-3">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">คำถามที่พบบ่อย</h2>
            <div className="space-y-2">
              {g.faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-white/10 bg-white/5 p-4">
                  <summary className="cursor-pointer list-none text-sm font-bold text-white">
                    Q{i + 1}. {f.q}
                  </summary>
                  <p className="mt-2 text-sm text-white/80">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {related.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">บริการที่เกี่ยวข้อง</h2>
              <ul className="grid gap-3 sm:grid-cols-2">
                {related.map((s) => (
                  <li key={s.id}>
                    <Link
                      to="/services/$service"
                      params={{ service: s.slug }}
                      className="block rounded-xl border border-white/10 bg-white/5 p-4 transition hover:border-gold/40 hover:bg-white/[0.08]"
                    >
                      <div className="text-sm font-bold text-white">{s.nameTh}</div>
                      <div className="mt-1 text-xs text-white/80">{s.shortTh}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="space-y-3">
            <h2 className="text-xl font-bold text-white">คู่มืออื่นที่น่าอ่าน</h2>
            <ul className="grid gap-3 sm:grid-cols-3">
              {otherGuides.map((og) => (
                <li key={og.slug}>
                  <Link
                    to="/guide/$slug"
                    params={{ slug: og.slug }}
                    className="block rounded-xl border border-white/10 bg-white/5 p-4 transition hover:border-gold/40 hover:bg-white/[0.08]"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gold/80">
                      {og.category}
                    </div>
                    <div className="mt-1 text-sm font-bold text-white">{og.h1}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section id="sources" className="space-y-3">
            <h2 className="text-xl font-bold text-white">แหล่งอ้างอิง (Citations)</h2>
            <ul className="space-y-1 text-sm">
              {g.citations.map((c) => (
                <li key={c.url}>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-gold underline-offset-4 hover:underline"
                  >
                    {c.name} <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/15 via-white/5 to-transparent p-6 sm:p-8">
            <h2 className="text-xl font-extrabold sm:text-2xl">
              ต้องการความช่วยเหลือเรื่อง {g.category}?
            </h2>
            <p className="mt-2 text-sm text-white/80">
              ทีมงาน NYC พร้อมให้คำปรึกษาฟรีและประเมินเอกสารภายใน 15 นาที
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/quote"
                className="rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep hover:bg-gold/90"
              >
                ขอใบเสนอราคา
              </Link>
              <a
                href="tel:+66832494999"
                className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-bold text-white hover:bg-white/10"
              >
                โทร 083-249-4999
              </a>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
